import type {
  FormElement,
  FormData,
  ValidationResult,
  ValidationRule,
  FormElementProperty,
} from '../types';

/**
 * Validation Engine for MediForm SDK
 *
 * Validates form data against form definition rules
 */

export class FormValidator {
  private formDefinition: FormElement;
  private errors: Record<string, string[]>;

  constructor(formDefinition: FormElement) {
    this.formDefinition = formDefinition;
    this.errors = {};
  }

  /**
   * Validate all form data
   */
  validate(data: FormData): ValidationResult {
    this.errors = {};
    this.validateElement(this.formDefinition, data);

    return {
      valid: Object.keys(this.errors).length === 0,
      errors: this.errors,
    };
  }

  /**
   * Validate a single field
   */
  validateField(fieldId: string, value: any): string[] {
    const element = this.findElementById(this.formDefinition, fieldId);
    if (!element) return [];

    const fieldErrors: string[] = [];
    this.validateFieldRules(element, value, fieldErrors);

    return fieldErrors;
  }

  /**
   * Recursively validate elements
   */
  private validateElement(element: FormElement, data: FormData): void {
    const properties = element.properties;

    // Only validate field elements (not containers)
    if (this.isField(element.type)) {
      const value = data[element.id];
      const fieldErrors: string[] = [];

      this.validateFieldRules(element, value, fieldErrors);

      if (fieldErrors.length > 0) {
        this.errors[element.id] = fieldErrors;
      }
    }

    // Special handling for questionnaire-grid
    if (element.type === 'questionnaire-grid' && properties?.gridRows) {
      properties.gridRows.forEach(row => {
        const value = data[row.id];
        const fieldErrors: string[] = [];

        if (properties.required && !value) {
          fieldErrors.push(`${row.question} is required`);
        }

        if (fieldErrors.length > 0) {
          this.errors[row.id] = fieldErrors;
        }
      });
    }

    // Recursively validate children
    if (element.children) {
      element.children.forEach(child => this.validateElement(child, data));
    }
  }

  /**
   * Validate field-level rules
   */
  private validateFieldRules(
    element: FormElement,
    value: any,
    errors: string[]
  ): void {
    const properties = element.properties;
    if (!properties) return;

    const label = properties.label || properties.heading || properties.name || 'This field';

    // Required validation
    if (properties.required) {
      if (this.isEmpty(value)) {
        errors.push(`${label} is required`);
        return; // Skip other validations if required and empty
      }
    }

    // Skip other validations if field is empty and not required
    if (this.isEmpty(value)) return;

    // Min/Max length for text fields
    if (properties.minLength && typeof value === 'string') {
      if (value.length < properties.minLength) {
        errors.push(`${label} must be at least ${properties.minLength} characters`);
      }
    }

    if (properties.maxLength && typeof value === 'string') {
      if (value.length > properties.maxLength) {
        errors.push(`${label} must not exceed ${properties.maxLength} characters`);
      }
    }

    // Min/Max value for number fields
    if (properties.min !== undefined && typeof value === 'number') {
      if (value < properties.min) {
        errors.push(`${label} must be at least ${properties.min}`);
      }
    }

    if (properties.max !== undefined && typeof value === 'number') {
      if (value > properties.max) {
        errors.push(`${label} must not exceed ${properties.max}`);
      }
    }

    // Pattern validation
    if (properties.pattern && typeof value === 'string') {
      try {
        const regex = new RegExp(properties.pattern);
        if (!regex.test(value)) {
          errors.push(`${label} format is invalid`);
        }
      } catch (e) {
        console.error('Invalid regex pattern:', properties.pattern);
      }
    }

    // Decimal places validation for numbers
    if (properties.decimalPlaces !== undefined && typeof value === 'number') {
      const decimalPart = value.toString().split('.')[1];
      if (decimalPart && decimalPart.length > properties.decimalPlaces) {
        errors.push(
          `${label} must have at most ${properties.decimalPlaces} decimal place(s)`
        );
      }
    }

    // Custom validation rules
    if (properties.validationRules) {
      properties.validationRules.forEach(rule => {
        this.validateRule(rule, value, label, errors);
      });
    }

    // Custom validation expression
    if (properties.customValidation) {
      try {
        // Safely evaluate custom validation (simplified)
        // In production, use a safe expression evaluator
        const result = this.evaluateCustomValidation(
          properties.customValidation,
          value
        );
        if (!result) {
          errors.push(`${label} validation failed`);
        }
      } catch (e) {
        console.error('Error in custom validation:', e);
      }
    }
  }

  /**
   * Validate individual validation rule
   */
  private validateRule(
    rule: ValidationRule,
    value: any,
    label: string,
    errors: string[]
  ): void {
    switch (rule.type) {
      case 'pattern':
        if (typeof value === 'string' && typeof rule.value === 'string') {
          try {
            const regex = new RegExp(rule.value);
            if (!regex.test(value)) {
              errors.push(rule.message);
            }
          } catch (e) {
            console.error('Invalid regex in validation rule:', rule.value);
          }
        }
        break;

      case 'minLength':
        if (typeof value === 'string' && typeof rule.value === 'number') {
          if (value.length < rule.value) {
            errors.push(rule.message);
          }
        }
        break;

      case 'maxLength':
        if (typeof value === 'string' && typeof rule.value === 'number') {
          if (value.length > rule.value) {
            errors.push(rule.message);
          }
        }
        break;

      case 'min':
        if (typeof value === 'number' && typeof rule.value === 'number') {
          if (value < rule.value) {
            errors.push(rule.message);
          }
        }
        break;

      case 'max':
        if (typeof value === 'number' && typeof rule.value === 'number') {
          if (value > rule.value) {
            errors.push(rule.message);
          }
        }
        break;

      case 'custom':
        // Custom validation would be evaluated here
        // For now, just a placeholder
        break;
    }
  }

  /**
   * Evaluate custom validation expression
   * Note: This is a simplified version. In production, use a safe expression evaluator
   */
  private evaluateCustomValidation(expression: string, value: any): boolean {
    try {
      // Create a safe context with only the value
      const context = { value };

      // Very basic evaluation - replace with proper expression evaluator
      // For security, never use eval() in production
      // Consider using libraries like expr-eval or jsep
      if (expression.includes('value')) {
        // Simple validation for demonstration
        return true;
      }

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Check if value is empty
   */
  private isEmpty(value: any): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
  }

  /**
   * Check if element type is a field (not a container)
   */
  private isField(type: string): boolean {
    const fieldTypes = [
      'text',
      'number',
      'textarea',
      'radio',
      'checkbox',
      'single-checkbox',
      'date',
      'yes-no',
    ];
    return fieldTypes.includes(type);
  }

  /**
   * Find element by ID in the form tree
   */
  private findElementById(element: FormElement, id: string): FormElement | null {
    if (element.id === id) return element;

    if (element.children) {
      for (const child of element.children) {
        const found = this.findElementById(child, id);
        if (found) return found;
      }
    }

    return null;
  }
}

/**
 * Helper function to validate form data
 */
export function validateForm(
  formDefinition: FormElement,
  data: FormData
): ValidationResult {
  const validator = new FormValidator(formDefinition);
  return validator.validate(data);
}

/**
 * Helper function to validate a single field
 */
export function validateField(
  formDefinition: FormElement,
  fieldId: string,
  value: any
): string[] {
  const validator = new FormValidator(formDefinition);
  return validator.validateField(fieldId, value);
}
